import * as React from "react";
import { useBrandingContext } from "@/contexts/BrandingContext";
import { ErrorPane } from "@/components/organisms/ErrorPane";
import { IKycApi } from "@/services/api/kycApi";
import { createOnfidoConfig, initOnfido } from "@/services/onfido/onfidoConfig";
import { Handle } from "onfido-sdk-ui/types/Onfido";

export type KYCProps = {
  api: IKycApi;
  setBusy: (busy: boolean) => void;
  onDone: () => void;
  faceSimilarityNeeded?: boolean;
};

export const Kyc: React.FC<KYCProps> = ({ api, setBusy, onDone, faceSimilarityNeeded = false }) => {
  const [state, setState] = React.useState({
    applicantId: null as string | null,
    token: null as string | null,
  });

  const [unrecoverableError, setUnrecoverableError] = React.useState<Error | null>(null);
  const { brand } = useBrandingContext();
  const [retryCount, setRetryCount] = React.useState(0);

  // Use ref to track if Onfido is initialized to prevent double initialization
  const onfidoRef = React.useRef<Handle | null>(null);
  const checkIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const refresh = React.useCallback(() => {
    setRetryCount((c) => c + 1);
  }, []);

  const { applicantId, token } = state;

  const done = React.useCallback(
    (checkId: string) => {
      api
        .downloadCheck({ checkId })
        .catch((error) => {
          console.warn(error);
        })
        .then(() => {
          setBusy(false);
          onDone();
        });
    },
    [api, setBusy, onDone]
  );

  // Effect to get applicant and token
  React.useEffect(() => {
    let isMounted = true;

    api.getApplicantAndToken().then(
      (response) => {
        if (!isMounted) return;
        if (response.error) {
          setUnrecoverableError(response.error);
          return;
        }
        const { applicantId, token } = response.data;
        setState({
          applicantId,
          token,
        });
      },
      (error) => {
        if (!isMounted) return;
        setUnrecoverableError(error);
      }
    );

    return () => {
      isMounted = false;
    };
  }, [api, retryCount]);

  // Effect to initialize Onfido SDK
  React.useEffect(() => {
    if (!applicantId || !token) return;

    // Prevent double initialization
    if (onfidoRef.current) {
      return;
    }

    const config = createOnfidoConfig({
      brand,
      token,
      faceSimilarityNeeded,
      onComplete: (data: any) => {
        if (onfidoRef.current) {
          onfidoRef.current.tearDown();
          onfidoRef.current = null;
        }
        setBusy(true);

        callWithReference((repeat) => {
          api.createChecks({ applicantId, variant: data.face?.variant }).then(
            (response) => {
              if (response.error) {
                console.warn(response.error);
                return;
              }

              const { data } = response;

              checkIntervalRef.current = setInterval(() => {
                const { checkId } = data;
                api.getChecks({ checkId }).then(
                  (response) => {
                    if (response.error) {
                      console.warn(response.error);
                      return;
                    }
                    const { data } = response;
                    if (data.status !== "complete") {
                      return;
                    }

                    if (checkIntervalRef.current) {
                      clearInterval(checkIntervalRef.current);
                      checkIntervalRef.current = null;
                    }

                    if (data.result !== "consider") {
                      done(checkId);
                    } else {
                      api.getReports({ reportIds: data.reportIds }).then(
                        (results) => {
                          if (
                            results.some(
                              (result) => result.error || result.data.subResult === "rejected"
                            )
                          ) {
                            setBusy(false);
                            refresh();
                            window.alert("Verification failed; please try again.");
                            return;
                          }

                          done(checkId);
                        },

                        (error) => {
                          console.warn(error);
                          window.alert("Verification failed; please try again.");
                        }
                      );
                    }
                  },
                  (error) => {
                    console.warn(error);
                  }
                );
              }, 2000);
            },
            (_) => repeat()
          );
        });
      },

      onError: (error: any) => {
        console.error("Onfido SDK error:", error);
        if (error.type === "expired_token") return;
        // exception
      },
    });

    onfidoRef.current = initOnfido(config);

    // Cleanup function
    return () => {
      if (onfidoRef.current) {
        onfidoRef.current.tearDown();
        onfidoRef.current = null;
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [api, applicantId, token, setBusy, done, refresh, brand, faceSimilarityNeeded]);

  if (unrecoverableError) {
    return (
      <ErrorPane
        message="Unexpected failure"
        description="Please reload the page. If the problem persists, please contact customer support."
      />
    );
  }

  return <div id="onfido-mount"></div>;
};

const callWithReference = (proc: (a: () => void) => void) => {
  const wrapper = () => {
    proc(wrapper);
  };
  return proc(wrapper);
};
